/**
 * Tenant theme stylesheet editor (feature plan 0054, step 1).
 *
 * Renders `/settings/theme`: the tenant's CSS-variable overrides in a plain `<o2d-textarea>` (step 2
 * replaces it with the line-numbered `@o2d/code-editor`), the validator's errors line by line ABOVE
 * the field — each one jumps to its line —, a preview that applies the draft to this tab only (a reload drops it), and
 * a save that dispatches `save.tenant-theme`.
 *
 * The decisions (when may the user type, save, preview; which failure message) live in
 * `TenantThemeEditorState`, the rules in `TenantThemeCssHelper`, the preview mechanics in
 * `TenantThemePreview` — this element only wires them.
 *
 * Extends `AbstractO2dElement` (Light DOM, ADR-007).
 */

import { AbstractO2dElement } from '@o2d/shared';
// Side-effect import: registers `<o2d-textarea>` (the tag string is not exported). Its CSS lives in
// textarea.css (see the translation editor for the history); this view adds its own height rule in
// tenant-theme-editor.css.
import '@o2d/textarea';
import { get } from 'lit-translate';
import { html } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { state } from 'lit/decorators/state.js';
import { container } from 'tsyringe';
import { hasPrivilege } from '../helpers/privilege.helper';
import { CommandName } from '../commands/command-name.enum';
import { sanitizeTenant } from '../components/ha-app/tenant-context.helper';
import { CommandBus } from '../services/command-bus.service';
import { GlobalNotificationService } from '../services/global-notification.service';
import { SaveTenantThemeCommand } from '../commands/save-tenant-theme.command';
import { TenantThemeContent } from '../commands/payloads/tenant-theme-content';
import { TenantThemeCssHelper } from '../helpers/tenant-theme-css.helper';
import type { TenantThemeCssError } from '../helpers/tenant-theme-css.helper';
import { TenantThemeEditorState } from '../helpers/tenant-theme-editor.state';
import type { ThemeLoadState } from '../helpers/tenant-theme-editor.state';
import { TENANT_THEME_LINK_ID, TENANT_THEME_PATH, TenantThemePreview } from '../helpers/tenant-theme-preview';
import { UiHelper } from '../helpers/ui.helper';
import { NOTIFICATION_DURATION } from '../constants';

/** After a save the preview ends when the re-pointed theme link has loaded — or after this, at the latest. */
const LINK_RELOAD_TIMEOUT_MS = 5_000;

@customElement('ha-tenant-theme-editor-view')
export class HaTenantThemeEditorView extends AbstractO2dElement {

    @state() private _load: ThemeLoadState = 'loading';

    @state() private _draft = '';

    @state() private _saved = '';

    @state() private _errors: TenantThemeCssError[] = [];

    @state() private _saving = false;

    @state() private _previewing = false;

    private _loadStarted = false;

    /**
     * Ends a still pending post-save reload (review 0054-02): its `load`, `error` or timeout would
     * otherwise stop a preview the user started in the meantime.
     */
    private _cancelReload: (() => void) | null = null;

    private readonly _preview = new TenantThemePreview(
        document,
        window.location.origin,
        options => new CSSStyleSheet(options),
    );

    /** Warns before leaving with unsaved edits; stable reference so disconnectedCallback removes the same one. */
    private readonly _onBeforeUnload = (event: BeforeUnloadEvent): void => {
        if (TenantThemeEditorState.isDirty(this._state())) {
            event.preventDefault();
            event.returnValue = '';
        }
    };

    public connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('beforeunload', this._onBeforeUnload);

        if (!this._loadStarted) {
            this._loadStarted = true;
            void this._loadTheme();
        }
    }

    public disconnectedCallback(): void {
        window.removeEventListener('beforeunload', this._onBeforeUnload);
        // Leaving the page ends the preview and any pending reload; a reload of the page drops the
        // preview anyway (it only lives in this document).
        this._cancelReload?.();
        this._preview.stop();
        this._previewing = false;
        super.disconnectedCallback();
    }

    private _state() {
        return {
            load: this._load,
            draft: this._draft,
            saved: this._saved,
            errorCount: this._errors.length,
            saving: this._saving,
        };
    }

    private async _loadTheme(): Promise<void> {
        try {
            const response = await fetch(TENANT_THEME_PATH, { cache: 'no-store' });
            if (!TenantThemeEditorState.isThemeResponse(response)) {
                throw new Error(`unexpected theme response ${response.status}`);
            }
            const css = await response.text();
            this._saved = css;
            this._draft = css;
            this._errors = TenantThemeCssHelper.validate(css);
            this._load = 'loaded';
        } catch (error) {
            console.error(error);
            this._load = 'failed';
            container.resolve(GlobalNotificationService).error(
                get('tenantThemeEditor.load.errorTitle'),
                get('tenantThemeEditor.load.errorMessage'),
            );
        }
    }

    private readonly _onInput = (event: CustomEvent<{ value: string }>): void => {
        this._draft = event.detail.value;
        this._errors = TenantThemeCssHelper.validate(this._draft);
        // A running preview follows the draft while it stays valid; an invalid draft ends it. Typing
        // while the post-save reload is pending takes the reload over: its load must not end the
        // preview the user is editing again (review 0054-04).
        if (this._previewing) {
            this._cancelReload?.();
            if (this._errors.length === 0) {
                this._preview.start(this._draft);
            } else {
                this._stopPreview();
            }
        }
    };

    private readonly _onPreview = (): void => {
        if (!TenantThemeEditorState.canPreview(this._state())) {
            return;
        }
        this._cancelReload?.();
        this._preview.start(this._draft);
        this._previewing = true;
    };

    private readonly _stopPreview = (): void => {
        this._preview.stop();
        this._previewing = false;
    };

    /** Focuses the field and selects the line of an error (Light DOM, so the inner textarea is reachable). */
    private _jumpToLine(line: number): void {
        const textarea = this.querySelector<HTMLTextAreaElement>('o2d-textarea textarea');
        if (!textarea) {
            return;
        }
        const [start, end] = TenantThemeCssHelper.lineRange(this._draft, line);
        textarea.focus();
        textarea.setSelectionRange(start, end);
    }

    private readonly _onSave = async (): Promise<void> => {
        if (!TenantThemeEditorState.canSave(this._state())) {
            return;
        }
        this._saving = true;
        const css = this._draft;
        try {
            const tenant = sanitizeTenant(window.__APP_CONTEXT__);
            const command = new SaveTenantThemeCommand(tenant ?? '', new TenantThemeContent(css));

            // No race against a timer (review 0054-01, R01-6): the CommandBus settles every failed
            // send itself, a throwing toDTO included (ADR-016). A timer could only claim a failure
            // for a command still queued that lands a moment later.
            const result = await container.resolve(CommandBus).dispatchCommandForResult(command);
            if (!result.success) {
                const forbidden = TenantThemeEditorState.saveFailureKind(result.error) === 'forbidden';
                container.resolve(GlobalNotificationService).error(
                    get('tenantThemeEditor.save.errorTitle'),
                    get(forbidden ? 'tenantThemeEditor.save.forbiddenMessage' : 'tenantThemeEditor.save.errorMessage'),
                );
                return;
            }

            this._saved = css;
            this._reloadThemeLink();

            UiHelper.notify({
                closable: true,
                level: 'info',
                title: get('tenantThemeEditor.save.successTitle'),
                content: html`<p>${get('tenantThemeEditor.save.successMessage')}</p>`,
                duration: NOTIFICATION_DURATION,
            });
        } finally {
            this._saving = false;
        }
    };

    /**
     * Points the theme link at the saved file. The link gets its state back FIRST — with the disabled
     * attribute it would not load at all — and the preview ends only once the new sheet is there, so
     * the page never flashes the old theme in between.
     */
    private _reloadThemeLink(): void {
        // At most one reload at a time: a second save while the first reload is pending must not leave
        // the old one armed (review 0054-03, R03-1).
        this._cancelReload?.();
        const link = document.getElementById(TENANT_THEME_LINK_ID) as HTMLLinkElement | null;
        this._preview.restoreLink();
        if (!link) {
            this._stopPreview();
            return;
        }

        // Only one of load/error ever fires, and the timer outlives both: an AbortController takes both
        // listeners off and the timer is cleared, so nothing keeps this view alive after the reload
        // (review 0054-04, R04-3).
        const listeners = new AbortController();
        let timer = 0;
        const disarm = (): void => {
            listeners.abort();
            window.clearTimeout(timer);
            this._cancelReload = null;
        };
        const finish = (): void => {
            // An aborted listener or a cleared timer does not fire in a browser; the check keeps a
            // stale callback harmless anyway.
            if (listeners.signal.aborted) {
                return;
            }
            disarm();
            this._stopPreview();
        };
        this._cancelReload = disarm;
        link.addEventListener('load', finish, { once: true, signal: listeners.signal });
        link.addEventListener('error', finish, { once: true, signal: listeners.signal });
        timer = window.setTimeout(finish, LINK_RELOAD_TIMEOUT_MS);
        link.href = `${TENANT_THEME_PATH}?v=${Date.now()}`;
    }

    protected render(): TemplateResult {
        // Defense in depth on top of the route guard (e.g. the tag instantiated directly).
        if (!hasPrivilege(CommandName.SAVE_TENANT_THEME)) {
            return html``;
        }

        const current = this._state();

        return html`
            <div class="view__body tenant-theme-editor">
                <h2 class="tenant-theme-editor__title">${get('tenantThemeEditor.title')}</h2>
                <div class="tenant-theme-editor__hint">
                    <p>${get('tenantThemeEditor.hint.intro')}</p>
                    <p>${get('tenantThemeEditor.hint.rules')}</p>
                    <p>${get('tenantThemeEditor.hint.images')}</p>
                    <p>${get('tenantThemeEditor.hint.editing')}</p>
                    <p>${get('tenantThemeEditor.hint.public')}</p>
                </div>
                <div class="tenant-theme-editor__toolbar">
                    ${this._previewing
                        ? html`<button class="tenant-theme-editor__preview-stop" type="button" @click=${this._stopPreview}>
                                ${get('tenantThemeEditor.preview.stop')}
                            </button>`
                        : html`<button class="tenant-theme-editor__preview" type="button"
                                ?disabled=${!TenantThemeEditorState.canPreview(current)}
                                @click=${this._onPreview}>
                                ${get('tenantThemeEditor.preview.start')}
                            </button>`}
                    <button class="tenant-theme-editor__save" type="button"
                        ?disabled=${!TenantThemeEditorState.canSave(current)}
                        @click=${this._onSave}>
                        ${get('tenantThemeEditor.save.button')}
                    </button>
                </div>
                ${this._previewing
                    ? html`<p class="tenant-theme-editor__preview-active">${get('tenantThemeEditor.preview.active')}</p>`
                    : html``}
                ${this._renderErrors()}
                <o2d-textarea
                    class="tenant-theme-editor__field"
                    spellcheck="false"
                    label=${get('tenantThemeEditor.label')}
                    ?readonly=${TenantThemeEditorState.isReadonly(current)}
                    .value=${this._draft}
                    @value-changed=${this._onInput}
                ></o2d-textarea>
            </div>
        `;
    }

    /** The error list above the field; each entry is a button that jumps to its line. */
    private _renderErrors(): TemplateResult {
        return html`
            <div class="tenant-theme-editor__errors" aria-live="polite">
                ${this._errors.length === 0
                    ? html``
                    : html`
                        <h3 class="tenant-theme-editor__errors-heading">${get('tenantThemeEditor.errors.heading')}</h3>
                        <ul class="tenant-theme-editor__error-list">
                            ${this._errors.map(error => html`
                                <li>
                                    <button class="tenant-theme-editor__error" type="button"
                                        @click=${() => this._jumpToLine(error.line)}>
                                        ${get('tenantThemeEditor.errors.line')} ${error.line}: ${error.message}
                                    </button>
                                </li>
                            `)}
                        </ul>
                    `}
            </div>
        `;
    }
}
